import React, { useState, useEffect } from 'react';
import { DASHBOARD_STATS, OPEN_POSITIONS, RECENT_TRADES } from '../../mock';
import { useToast } from '../../hooks/use-toast';
import { Copy, Check, Plus, Trash2, Building2, Bitcoin, CreditCard, Landmark, ExternalLink, Info, Upload, X, ShieldCheck } from 'lucide-react';
import { txAPI, historyAPI, accountAPI, withdrawMethodsAPI, depositAccountsAPI, kycAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

export const Portfolio = () => {
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  useEffect(() => {
    accountAPI.summary().then(setSummary).catch(() => {});
    historyAPI.list(20).then(setTrades).catch(() => {});
  }, []);
  const balance = summary?.balance ?? 0;
  const realised = summary?.realised_pnl ?? 0;
  const allocation = [
    { name: 'Crypto Spot', val: 55, color: '#00e676' },
    { name: 'Forex', val: 22, color: '#22c55e' },
    { name: 'Stocks', val: 15, color: '#0ea5e9' },
    { name: 'Cash', val: 8, color: '#64748b' }
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Portfolio</h1>
        <p className="text-sm text-gray-400">Your holdings and allocations.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
          <h3 className="text-sm text-white font-bold mb-4">Allocation</h3>
          <div className="h-56">
            <MiniPie data={allocation} total={balance}/>
          </div>
          <div className="space-y-2 mt-4">
            {allocation.map(a => (
              <div key={a.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }}/>
                  <span className="text-gray-300">{a.name}</span>
                </div>
                <span className="text-white font-semibold mono">{a.val}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          {[
            { l: 'Total Balance', v: `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
            { l: 'Realised P&L', v: `${realised >= 0 ? '+' : ''}$${realised.toFixed(2)}`, positive: realised >= 0 },
            { l: 'Open Trades', v: summary?.open_positions ?? 0 },
            { l: 'Closed Trades', v: summary?.closed_trades ?? 0 }
          ].map(s => (
            <div key={s.l} className="p-5 rounded-xl border border-[#1a2823] bg-[#0a1210]">
              <div className="text-xs text-gray-400 uppercase tracking-wider">{s.l}</div>
              <div className={`mt-2 text-2xl font-extrabold mono ${s.positive === false ? 'text-red-400' : (s.positive ? 'text-[#00e676]' : 'text-white')}`}>{s.v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
        <h3 className="text-sm text-white font-bold mb-4">Recent Closed Trades</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="py-2">Symbol</th><th>Side</th><th>Volume</th><th>Open</th><th>Close</th><th>Closed At</th><th className="text-right">P&L</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 && <tr><td colSpan="7" className="py-6 text-center text-sm text-gray-500">No closed trades yet.</td></tr>}
              {trades.map(t => (
                <tr key={t.id} className="border-t border-[#141f1b]">
                  <td className="py-3 text-white font-semibold">{t.display || t.symbol}</td>
                  <td><span className={`px-2 py-0.5 rounded text-xs font-semibold ${t.side === 'Buy' ? 'bg-[#00e676]/15 text-[#00e676]' : 'bg-red-500/15 text-red-400'}`}>{t.side}</span></td>
                  <td className="mono text-gray-300">{t.volume}</td>
                  <td className="mono text-gray-300">{Number(t.open_price).toFixed(2)}</td>
                  <td className="mono text-white">{Number(t.close_price).toFixed(2)}</td>
                  <td className="text-gray-400 text-xs mono">{new Date(t.closed_at).toLocaleDateString()}</td>
                  <td className={`mono font-semibold text-right ${t.pnl >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{t.pnl >= 0 ? '+' : ''}${Number(t.pnl).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Simple SVG donut chart replacement
const MiniPie = ({ data, total }) => {
  const totalVal = data.reduce((a, d) => a + d.val, 0);
  let cum = 0;
  const cx = 100, cy = 100, r = 70, ir = 45;
  const arcs = data.map(d => {
    const start = (cum / totalVal) * 2 * Math.PI;
    cum += d.val;
    const end = (cum / totalVal) * 2 * Math.PI;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.sin(start), y1 = cy - r * Math.cos(start);
    const x2 = cx + r * Math.sin(end), y2 = cy - r * Math.cos(end);
    const xi2 = cx + ir * Math.sin(end), yi2 = cy - ir * Math.cos(end);
    const xi1 = cx + ir * Math.sin(start), yi1 = cy - ir * Math.cos(start);
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${ir} ${ir} 0 ${large} 0 ${xi1} ${yi1} Z`;
  });
  const label = total >= 1000 ? `$${(total / 1000).toFixed(1)}K` : `$${total.toFixed(0)}`;
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full">
      {arcs.map((a, i) => <path key={data[i].name} d={a} fill={data[i].color} opacity="0.9"/>)}
      <text x="100" y="96" textAnchor="middle" fill="#94a3b8" fontSize="11">Total</text>
      <text x="100" y="116" textAnchor="middle" fill="#fff" fontSize="18" fontWeight="800">{label}</text>
    </svg>
  );
};

const DEPOSIT_TYPE_META = {
  crypto: { icon: Bitcoin,   label: 'Crypto',       tone: 'text-amber-400' },
  bank:   { icon: Building2, label: 'Bank Account', tone: 'text-sky-400' },
  card:   { icon: CreditCard,label: 'Card',         tone: 'text-purple-400' },
  wire:   { icon: Landmark,  label: 'Wire Transfer',tone: 'text-[#00e676]' }
};

const DetailRow = ({ label, value, onCopy, testid }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    onCopy?.();
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-[#141f1b] last:border-0">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap pt-1 min-w-[110px]">{label}</div>
      <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
        <code data-testid={testid} className="text-xs text-white mono break-all text-right">{value}</code>
        <button onClick={copy} className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-[#00e676] hover:border-[#00e676] shrink-0" title="Copy">
          {copied ? <Check size={11}/> : <Copy size={11}/>}
        </button>
      </div>
    </div>
  );
};

const DepositAccountCard = ({ acc }) => {
  const meta = DEPOSIT_TYPE_META[acc.type] || DEPOSIT_TYPE_META.crypto;
  const Ico = meta.icon;
  return (
    <div className="p-4 rounded-lg bg-[#050807] border border-[#1a2823]" data-testid={`deposit-account-card-${acc.id}`}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg bg-[#00e676]/10 border border-[#00e676]/30 flex items-center justify-center ${meta.tone}`}>
          <Ico size={14}/>
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-white">{acc.label}</div>
          <div className={`text-[10px] uppercase tracking-widest ${meta.tone} font-semibold`}>{meta.label}</div>
        </div>
        {acc.min_amount && <div className="text-[10px] text-gray-500">Min ${acc.min_amount}</div>}
      </div>

      <div className="space-y-0">
        {acc.type === 'crypto' && (
          <>
            <DetailRow label="Network"  value={acc.network} testid={`crypto-network-${acc.id}`}/>
            <DetailRow label="Address"  value={acc.address} testid={`crypto-address-${acc.id}`}/>
            <DetailRow label="Memo/Tag" value={acc.memo_tag} testid={`crypto-memo-${acc.id}`}/>
          </>
        )}
        {acc.type === 'bank' && (
          <>
            <DetailRow label="Bank"     value={acc.bank_name}      testid={`bank-name-${acc.id}`}/>
            <DetailRow label="Holder"   value={acc.account_holder} testid={`bank-holder-${acc.id}`}/>
            <DetailRow label="Account"  value={acc.account_number} testid={`bank-account-${acc.id}`}/>
            <DetailRow label="Routing"  value={acc.routing_number} testid={`bank-routing-${acc.id}`}/>
            <DetailRow label="SWIFT"    value={acc.swift_code}     testid={`bank-swift-${acc.id}`}/>
            <DetailRow label="IBAN"     value={acc.iban}           testid={`bank-iban-${acc.id}`}/>
          </>
        )}
        {acc.type === 'wire' && (
          <>
            <DetailRow label="Bank"        value={acc.bank_name}          testid={`wire-bank-${acc.id}`}/>
            <DetailRow label="Beneficiary" value={acc.account_holder}     testid={`wire-holder-${acc.id}`}/>
            <DetailRow label="IBAN"        value={acc.iban}               testid={`wire-iban-${acc.id}`}/>
            <DetailRow label="Account"     value={acc.account_number}     testid={`wire-account-${acc.id}`}/>
            <DetailRow label="SWIFT"       value={acc.swift_code}         testid={`wire-swift-${acc.id}`}/>
            <DetailRow label="Correspond." value={acc.correspondent_bank} testid={`wire-corr-${acc.id}`}/>
            <DetailRow label="Address"     value={acc.beneficiary_address} testid={`wire-address-${acc.id}`}/>
            <DetailRow label="Reference"   value={acc.reference}          testid={`wire-ref-${acc.id}`}/>
          </>
        )}
        {acc.type === 'card' && (
          <>
            <DetailRow label="Processor" value={acc.processor_name} testid={`card-processor-${acc.id}`}/>
            {acc.processor_url && (
              <a href={acc.processor_url} target="_blank" rel="noopener noreferrer"
                 data-testid={`card-checkout-${acc.id}`}
                 className="mt-3 btn-green w-full py-2.5 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2">
                Open Hosted Checkout <ExternalLink size={12}/>
              </a>
            )}
          </>
        )}
      </div>

      {acc.instructions && (
        <div className="mt-3 p-2.5 rounded bg-[#0a1a13] border border-[#00e676]/20 text-[11px] text-gray-300 flex items-start gap-2">
          <Info size={11} className="text-[#00e676] mt-0.5 shrink-0"/>
          <span>{acc.instructions}</span>
        </div>
      )}
    </div>
  );
};

export const Deposit = () => {
  const [amount, setAmount] = useState('500');
  const [method, setMethod] = useState('crypto');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [loadingAccts, setLoadingAccts] = useState(true);
  const [loading, setLoading] = useState(false);
  // Proof of payment
  const [txHash, setTxHash] = useState('');
  const [senderRef, setSenderRef] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptDataUrl, setReceiptDataUrl] = useState('');
  const { toast } = useToast();
  const { refresh } = useAuth();

  useEffect(() => {
    depositAccountsAPI.listActive()
      .then(list => {
        setAccounts(list);
        const firstAvailable = ['crypto', 'bank', 'wire', 'card'].find(t => list.some(a => a.type === t));
        if (firstAvailable) setMethod(firstAvailable);
      })
      .catch(() => {})
      .finally(() => setLoadingAccts(false));
  }, []);

  const filtered = accounts.filter(a => a.type === method);
  useEffect(() => {
    // Auto-select the first account for the current method
    setSelectedAccountId(filtered[0]?.id || '');
    // Clear proof fields when method changes
    setTxHash(''); setSenderRef(''); setReceiptFile(null); setReceiptDataUrl('');
  }, [method, accounts]); // eslint-disable-line

  const handleReceipt = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setReceiptFile(null); setReceiptDataUrl(''); return; }
    if (file.size > 2 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Receipt must be under 2MB.' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please upload an image (JPG/PNG).' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setReceiptFile(file); setReceiptDataUrl(reader.result); };
    reader.readAsDataURL(file);
  };

  const proofLabel = method === 'crypto' ? 'Transaction Hash (TXID)'
                    : method === 'wire'   ? 'Wire Reference Number'
                    : method === 'bank'   ? 'Bank Transfer Reference'
                    : 'Order ID (from card processor)';
  const proofPlaceholder = method === 'crypto' ? '0xabc123… or bc1qxyz…'
                          : method === 'wire' ? 'e.g. FT2402NEXUS0918'
                          : method === 'bank' ? 'Your transfer reference'
                          : 'e.g. pi_3O8xxx…';

  const requiresProof = method !== 'card';
  const proofProvided = txHash.trim() || senderRef.trim() || receiptDataUrl;

  const submit = async () => {
    if (parseFloat(amount) < 20) { toast({ title: 'Minimum $20', description: 'Please enter at least $20.' }); return; }
    if (filtered.length === 0) { toast({ title: 'No destination', description: 'No deposit destinations configured for this method.' }); return; }
    if (requiresProof && !proofProvided) {
      toast({ title: 'Proof of payment required', description: 'Please enter the TX hash / reference or upload a receipt image.' });
      return;
    }
    const selectedAcc = filtered.find(a => a.id === selectedAccountId) || filtered[0];
    setLoading(true);
    try {
      const payload = {
        type: 'deposit',
        amount: parseFloat(amount),
        method,
        deposit_account_id: selectedAcc?.id,
        deposit_account_label: selectedAcc?.label
      };
      if (method === 'crypto') payload.tx_hash = txHash.trim();
      if (senderRef.trim())    payload.sender_reference = senderRef.trim();
      if (receiptDataUrl)      payload.receipt_url = receiptDataUrl;
      // For wire/bank, also stash the reference in tx_hash-equivalent field if user typed one
      if (method !== 'crypto' && txHash.trim() && !payload.sender_reference) {
        payload.sender_reference = txHash.trim();
      }
      await txAPI.create(payload);
      toast({ title: 'Deposit submitted', description: `Your $${amount} ${method} deposit is under review. Treasury will confirm shortly.` });
      setTxHash(''); setSenderRef(''); setReceiptFile(null); setReceiptDataUrl('');
      await refresh();
    } catch (e) {
      toast({ title: 'Deposit failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="deposit-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Deposit Funds</h1>
        <p className="text-sm text-gray-400">Send funds to a destination below, then submit proof of payment. Treasury reviews and credits within 15 min (crypto/card) or 1-3 business days (bank/wire).</p>
      </div>
      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210] space-y-5">
        {/* Step 1: amount */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider">1. Amount (USD)</label>
          <input data-testid="deposit-amount-input" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-4 py-3 text-2xl font-bold text-white mono focus:outline-none focus:border-[#00e676]"/>
          <div className="mt-2 flex gap-2 flex-wrap">
            {['100', '500', '1000', '5000', '10000'].map(v => (
              <button key={v} onClick={() => setAmount(v)} className="px-3 py-1 rounded border border-[#1a2823] text-xs text-gray-400 hover:border-[#00e676] hover:text-[#00e676]">${v}</button>
            ))}
          </div>
        </div>

        {/* Step 2: method */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">2. Payment Method</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(DEPOSIT_TYPE_META).map(([k, m]) => {
              const Ico = m.icon;
              const count = accounts.filter(a => a.type === k).length;
              const disabled = count === 0;
              return (
                <button
                  key={k}
                  data-testid={`method-${k}-btn`}
                  onClick={() => !disabled && setMethod(k)}
                  disabled={disabled}
                  className={`py-3 rounded-lg font-semibold text-sm border transition inline-flex items-center justify-center gap-2 ${method === k ? 'border-[#00e676] bg-[#00e676]/10 text-[#00e676]' : 'border-[#1a2823] text-gray-400 hover:border-[#00e676]/40'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <Ico size={14}/> {m.label} {count > 0 && <span className="text-[10px] opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 3: destination */}
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">3. Send funds to</label>
          {loadingAccts && <div className="text-xs text-gray-400">Loading deposit destinations…</div>}
          {!loadingAccts && filtered.length === 0 && (
            <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-300 text-sm flex items-start gap-2" data-testid="no-deposit-methods">
              <Info size={14} className="mt-0.5 shrink-0"/>
              <div>No {DEPOSIT_TYPE_META[method].label.toLowerCase()} destinations configured yet. Please contact support or choose another method.</div>
            </div>
          )}
          {!loadingAccts && filtered.length > 1 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {filtered.map(a => (
                <button key={a.id} onClick={() => setSelectedAccountId(a.id)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${selectedAccountId === a.id ? 'bg-[#00e676] text-black border-[#00e676]' : 'border-[#1a2823] text-gray-400 hover:border-[#00e676]/40'}`}>
                  {a.label}
                </button>
              ))}
            </div>
          )}
          {!loadingAccts && filtered.length > 0 && (
            <DepositAccountCard acc={filtered.find(a => a.id === selectedAccountId) || filtered[0]}/>
          )}
        </div>

        {/* Step 4: proof of payment (skipped for card) */}
        {method !== 'card' && filtered.length > 0 && (
          <div className="p-4 rounded-lg border border-[#00e676]/30 bg-[#0a1a13] space-y-3" data-testid="proof-of-payment-section">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} className="text-[#00e676]"/>
              <label className="text-xs text-[#00e676] uppercase tracking-widest font-semibold">4. Proof of Payment</label>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">{proofLabel}</label>
              <input
                data-testid="tx-hash-input"
                value={txHash}
                onChange={e => setTxHash(e.target.value)}
                placeholder={proofPlaceholder}
                className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white mono focus:outline-none focus:border-[#00e676]"
              />
              <p className="text-[10px] text-gray-500 mt-1">Paste the on-chain transaction hash so treasury can auto-match your deposit.</p>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">Receipt / Screenshot (optional)</label>
              <div className="mt-1 flex items-center gap-3">
                <label className="btn-outline-green px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer inline-flex items-center gap-1">
                  <Upload size={12}/> {receiptFile ? 'Replace' : 'Upload image'}
                  <input data-testid="receipt-upload-input" type="file" accept="image/*" className="hidden" onChange={handleReceipt}/>
                </label>
                {receiptFile && (
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <img src={receiptDataUrl} alt="Receipt preview" className="w-10 h-10 object-cover rounded border border-[#1a2823]" data-testid="receipt-preview"/>
                    <span className="truncate max-w-[200px]">{receiptFile.name}</span>
                    <button onClick={() => { setReceiptFile(null); setReceiptDataUrl(''); }} className="text-red-400 hover:text-red-300" data-testid="remove-receipt-btn">
                      <X size={12}/>
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-500 mt-1">Attach a screenshot of the transaction if you don't have a TX hash. Max 2MB.</p>
            </div>
          </div>
        )}

        <button onClick={submit} disabled={loading || filtered.length === 0} data-testid="submit-deposit-btn" className="btn-green w-full py-3 rounded-lg font-bold disabled:opacity-70">
          {loading ? 'Submitting…' : `Submit $${amount || 0} Deposit for Review`}
        </button>
        <p className="text-xs text-gray-500">Min deposit $20. Your deposit stays <span className="text-amber-300 font-semibold">pending</span> until treasury confirms it. Track status on the <span className="text-[#00e676]">History</span> page.</p>
      </div>
    </div>
  );
};

export const Withdraw = () => {
  const [amount, setAmount] = useState('100');
  const [loading, setLoading] = useState(false);
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [tab, setTab] = useState('bank');
  // Bank fields
  const [bankLabel, setBankLabel] = useState('');
  const [bankHolder, setBankHolder] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankRouting, setBankRouting] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankSwift, setBankSwift] = useState('');
  // Crypto fields
  const [cryptoLabel, setCryptoLabel] = useState('');
  const [cryptoNetwork, setCryptoNetwork] = useState('USDT-TRC20');
  const [cryptoAddr, setCryptoAddr] = useState('');
  // Card fields
  const [cardLabel, setCardLabel] = useState('');
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [kycStatus, setKycStatus] = useState(null);
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const balance = user?.balance ?? 0;
  const kycApproved = kycStatus === 'approved';

  const loadMethods = async () => {
    try {
      const list = await withdrawMethodsAPI.list();
      setMethods(list);
      if (list.length > 0 && !selectedMethod) setSelectedMethod(list[0].id);
    } catch {}
  };

  const loadKyc = async () => {
    try { const k = await kycAPI.me(); setKycStatus(k?.status || 'not_started'); } catch {}
  };

  useEffect(() => { loadMethods(); loadKyc(); }, []);

  const submit = async () => {
    if (!kycApproved) { toast({ title: 'KYC required', description: 'Complete KYC verification before withdrawing.' }); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast({ title: 'Invalid amount' }); return; }
    if (amt > balance) { toast({ title: 'Insufficient balance', description: `You have $${balance.toFixed(2)} available.` }); return; }
    if (!selectedMethod) { toast({ title: 'No destination', description: 'Please add a withdrawal method first.' }); return; }
    const m = methods.find(x => x.id === selectedMethod);
    setLoading(true);
    try {
      await txAPI.create({
        type: 'withdraw',
        amount: amt,
        method: m?.type || 'bank',
        destination: m?.label
      });
      toast({ title: 'Withdrawal requested', description: `$${amt} to ${m?.label} will arrive within 15 min – 24 hours.` });
      await refresh();
    } catch (e) {
      toast({ title: 'Withdrawal failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setLoading(false); }
  };

  const addMethod = async () => {
    try {
      let payload;
      if (tab === 'bank') {
        if (!bankLabel || !bankAccount) { toast({ title: 'Missing fields', description: 'Label & account number required' }); return; }
        payload = { type: 'bank', label: bankLabel, account_holder: bankHolder, account_number: bankAccount, routing_number: bankRouting, bank_name: bankName, swift_code: bankSwift };
      } else if (tab === 'crypto') {
        if (!cryptoLabel || !cryptoAddr) { toast({ title: 'Missing fields', description: 'Label & wallet address required' }); return; }
        payload = { type: 'crypto', label: cryptoLabel, network: cryptoNetwork, wallet_address: cryptoAddr };
      } else if (tab === 'card') {
        const cleaned = cardNumber.replace(/\s/g, '');
        if (!cardLabel || cleaned.length < 12) { toast({ title: 'Missing fields', description: 'Valid card number & label required' }); return; }
        payload = { type: 'card', label: cardLabel, card_holder: cardHolder, card_number: cleaned, card_expiry: cardExpiry };
      }
      const created = await withdrawMethodsAPI.add(payload);
      toast({ title: 'Withdrawal method added', description: created.label });
      setBankLabel(''); setBankHolder(''); setBankAccount(''); setBankRouting(''); setBankName(''); setBankSwift('');
      setCryptoLabel(''); setCryptoAddr('');
      setCardLabel(''); setCardHolder(''); setCardNumber(''); setCardExpiry('');
      setShowAdd(false);
      const list = await withdrawMethodsAPI.list();
      setMethods(list);
      setSelectedMethod(created.id);
    } catch (e) {
      toast({ title: 'Failed to add method', description: e?.response?.data?.detail || 'Try again' });
    }
  };

  const removeMethod = async (id) => {
    try {
      await withdrawMethodsAPI.remove(id);
      toast({ title: 'Method removed' });
      const list = await withdrawMethodsAPI.list();
      setMethods(list);
      if (selectedMethod === id) setSelectedMethod(list[0]?.id || '');
    } catch {}
  };

  const iconFor = (type) => type === 'bank' ? Building2 : type === 'crypto' ? Bitcoin : CreditCard;
  const methodSummary = (m) => {
    if (m.type === 'bank') return `${m.bank_name || 'Bank'} •••• ${m.account_last4 || '----'}`;
    if (m.type === 'crypto') return `${m.network} · ${m.address_masked}`;
    if (m.type === 'card') return `${m.card_number}`;
    return '';
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Withdraw Funds</h1>
        <p className="text-sm text-gray-400">Withdrawals processed in as little as 15 minutes.</p>
      </div>

      {!kycApproved && (
        <div data-testid="withdraw-kyc-gate" className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10 flex items-center gap-3">
          <ShieldCheck size={20} className="text-amber-400 flex-shrink-0"/>
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-400">KYC verification required</div>
            <div className="text-xs text-gray-300 mt-0.5">
              {kycStatus === 'pending'
                ? 'Your KYC is under review. Withdrawals unlock once it is approved.'
                : 'Verify your identity to enable withdrawals. This is a one-time step.'}
            </div>
          </div>
          <Link to="/dashboard/kyc" data-testid="withdraw-kyc-cta" className="px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 whitespace-nowrap">
            {kycStatus === 'pending' ? 'View Status' : 'Complete KYC →'}
          </Link>
        </div>
      )}

      {/* Withdraw amount */}
      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210] space-y-5">
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-[#050807] border border-[#1a2823]">
            <div className="text-xs text-gray-400">Available</div>
            <div className="mt-1 text-xl font-bold text-white mono">${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="p-4 rounded-lg bg-[#050807] border border-[#1a2823]">
            <div className="text-xs text-gray-400">Pending</div>
            <div className="mt-1 text-xl font-bold text-white mono">$0.00</div>
          </div>
          <div className="p-4 rounded-lg bg-[#050807] border border-[#1a2823]">
            <div className="text-xs text-gray-400">Fees</div>
            <div className="mt-1 text-xl font-bold text-[#00e676] mono">$0.00</div>
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider">Amount</label>
          <input value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-4 py-3 text-2xl font-bold text-white mono focus:outline-none focus:border-[#00e676]"/>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-gray-400 uppercase tracking-wider">Destination</label>
            <button onClick={() => setShowAdd(v => !v)} className="text-xs text-[#00e676] font-semibold flex items-center gap-1 hover:underline"><Plus size={12}/> Add Method</button>
          </div>
          {methods.length === 0 && !showAdd && (
            <div className="p-4 rounded-lg border border-dashed border-[#1a2823] text-center text-sm text-gray-500">
              No withdrawal methods yet. Click <span className="text-[#00e676] font-semibold">Add Method</span> above to add one.
            </div>
          )}
          {methods.length > 0 && (
            <div className="space-y-2">
              {methods.map(m => {
                const Ico = iconFor(m.type);
                return (
                  <label key={m.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${selectedMethod === m.id ? 'border-[#00e676] bg-[#00e676]/5' : 'border-[#1a2823] bg-[#050807] hover:border-[#00e676]/40'}`}>
                    <input type="radio" name="wm" checked={selectedMethod === m.id} onChange={() => setSelectedMethod(m.id)} className="accent-[#00e676]"/>
                    <div className="w-8 h-8 rounded-lg bg-[#00e676]/10 border border-[#00e676]/30 flex items-center justify-center text-[#00e676]">
                      <Ico size={15}/>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-white font-semibold">{m.label}</div>
                      <div className="text-[11px] text-gray-500 mono">{methodSummary(m)}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-gray-500">{m.type}</span>
                    <button onClick={(e) => { e.preventDefault(); removeMethod(m.id); }} className="p-1.5 rounded border border-[#1a2823] text-gray-400 hover:text-red-400 hover:border-red-500/40">
                      <Trash2 size={12}/>
                    </button>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <button onClick={submit} disabled={loading || !selectedMethod || !kycApproved} data-testid="request-withdrawal-btn" className="btn-green w-full py-3 rounded-lg font-bold disabled:opacity-50">
          {loading ? 'Processing…' : (!kycApproved ? 'Complete KYC to withdraw' : (selectedMethod ? 'Request Withdrawal' : 'Add a method first'))}
        </button>
      </div>

      {/* Add method form */}
      {showAdd && (
        <div className="p-6 rounded-xl border border-[#00e676]/20 bg-[#0a1210]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold">Add Withdrawal Method</h3>
            <button onClick={() => setShowAdd(false)} className="text-xs text-gray-400 hover:text-white">Cancel</button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { key: 'bank', label: 'Bank Account', Ico: Building2 },
              { key: 'crypto', label: 'Crypto Wallet', Ico: Bitcoin },
              { key: 'card', label: 'Card', Ico: CreditCard }
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`p-4 rounded-lg border font-semibold text-sm flex flex-col items-center gap-2 transition ${tab === t.key ? 'border-[#00e676] bg-[#00e676]/10 text-[#00e676]' : 'border-[#1a2823] text-gray-400 hover:border-[#00e676]/40'}`}>
                <t.Ico size={20}/>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'bank' && (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Label" value={bankLabel} onChange={setBankLabel} placeholder="e.g. Chase Checking"/>
              <Field label="Bank Name" value={bankName} onChange={setBankName} placeholder="e.g. Chase"/>
              <Field label="Account Holder" value={bankHolder} onChange={setBankHolder} placeholder="Full name"/>
              <Field label="Account Number" value={bankAccount} onChange={setBankAccount} placeholder="1234567890"/>
              <Field label="Routing Number" value={bankRouting} onChange={setBankRouting} placeholder="9-digit ACH"/>
              <Field label="SWIFT / BIC (intl)" value={bankSwift} onChange={setBankSwift} placeholder="Optional"/>
            </div>
          )}

          {tab === 'crypto' && (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Label" value={cryptoLabel} onChange={setCryptoLabel} placeholder="e.g. My USDT wallet"/>
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider">Network</label>
                <select value={cryptoNetwork} onChange={e => setCryptoNetwork(e.target.value)} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]">
                  <option>USDT-TRC20</option>
                  <option>USDT-ERC20</option>
                  <option>USDT-BEP20</option>
                  <option>USDC-ERC20</option>
                  <option>BTC</option>
                  <option>ETH</option>
                  <option>SOL</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <Field label="Wallet Address" value={cryptoAddr} onChange={setCryptoAddr} placeholder="Paste your wallet address"/>
              </div>
            </div>
          )}

          {tab === 'card' && (
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Label" value={cardLabel} onChange={setCardLabel} placeholder="e.g. Visa Card"/>
              <Field label="Cardholder Name" value={cardHolder} onChange={setCardHolder} placeholder="Full name on card"/>
              <Field label="Card Number" value={cardNumber} onChange={setCardNumber} placeholder="4242 4242 4242 4242"/>
              <Field label="Expiry (MM/YY)" value={cardExpiry} onChange={setCardExpiry} placeholder="12/29"/>
            </div>
          )}

          <button onClick={addMethod} className="btn-green mt-5 px-6 py-2.5 rounded-lg font-semibold text-sm">
            Save {tab === 'bank' ? 'Bank Account' : tab === 'crypto' ? 'Crypto Wallet' : 'Card'}
          </button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00e676]"/>
  </div>
);

export const History = () => {
  const [trades, setTrades] = useState([]);
  const [txs, setTxs] = useState([]);
  const [txFilter, setTxFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      historyAPI.list(200).then(setTrades).catch(() => {}),
      txAPI.list(200).then(setTxs).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const filteredTxs = txFilter === 'all' ? txs : txs.filter(t => (t.status || 'completed') === txFilter);
  const txCounts = {
    all: txs.length,
    pending: txs.filter(t => t.status === 'pending').length,
    completed: txs.filter(t => t.status === 'completed').length,
    rejected: txs.filter(t => t.status === 'rejected').length
  };

  const statusBadge = (s) => {
    const map = {
      completed: 'bg-[#00e676]/15 text-[#00e676]',
      pending: 'bg-amber-500/15 text-amber-400',
      rejected: 'bg-red-500/15 text-red-400'
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${map[s] || 'bg-gray-500/15 text-gray-400'}`}>{s || '—'}</span>;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">History</h1>
        <p className="text-sm text-gray-400">Your closed trades and wallet activity.</p>
      </div>

      {/* Deposit & Withdrawal history */}
      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
        <h3 className="text-white font-bold mb-4">Deposit &amp; Withdrawal History</h3>
        <div className="flex flex-wrap gap-2 mb-4" data-testid="tx-filter-tabs">
          {['all', 'pending', 'completed', 'rejected'].map(f => (
            <button key={f} onClick={() => setTxFilter(f)} data-testid={`tx-filter-${f}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize border transition ${txFilter === f ? 'border-[#00e676]/50 bg-[#00e676]/10 text-[#00e676]' : 'border-[#1a2823] text-gray-400 hover:text-white'}`}>
              {f} <span className="opacity-60">({txCounts[f]})</span>
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="transactions-history-table">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="py-2">Date</th><th>Type</th><th>Method</th><th>Reference</th><th className="text-right">Amount</th><th className="text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="6" className="py-8 text-center text-sm text-gray-500">Loading…</td></tr>}
              {!loading && filteredTxs.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-sm text-gray-500">{txs.length === 0 ? <>No deposits or withdrawals yet. <Link to="/dashboard/deposit" className="text-[#00e676] hover:underline">Make a deposit</Link>.</> : `No ${txFilter} transactions.`}</td></tr>}
              {filteredTxs.map(t => {
                const isDep = t.type === 'deposit';
                return (
                  <tr key={t.id} className="border-t border-[#141f1b]" data-testid={`tx-row-${t.id}`}>
                    <td className="py-3 text-gray-400 text-xs mono">{t.created_at ? new Date(t.created_at).toLocaleString('en-US') : '—'}</td>
                    <td><span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${isDep ? 'bg-[#00e676]/15 text-[#00e676]' : 'bg-orange-500/15 text-orange-400'}`}>{isDep ? 'Deposit' : 'Withdraw'}</span></td>
                    <td className="text-gray-300 capitalize">{t.method || '—'}</td>
                    <td className="text-gray-500 text-xs mono truncate max-w-[160px]">{t.sender_reference || '—'}</td>
                    <td className={`mono font-semibold text-right ${isDep ? 'text-[#00e676]' : 'text-orange-400'}`}>{isDep ? '+' : '-'}${Number(t.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-right">{statusBadge(t.status)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trade history */}
      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210]">
        <h3 className="text-white font-bold mb-4">Trade History</h3>
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
              <th className="py-2">Closed At</th><th>Market</th><th>Type</th><th>Symbol</th><th>Volume</th><th>Open</th><th>Close</th><th className="text-right">P&L</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-500">Loading…</td></tr>}
            {!loading && trades.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-sm text-gray-500">No closed trades yet. Start trading on the Terminal.</td></tr>}
            {trades.map(t => (
              <tr key={t.id} className="border-t border-[#141f1b]">
                <td className="py-3 text-gray-400 text-xs mono">{new Date(t.closed_at).toLocaleString('en-US')}</td>
                <td className="text-[10px] text-gray-500 uppercase">{t.market || 'spot'}</td>
                <td><span className={`px-2 py-0.5 rounded text-xs font-semibold ${t.side === 'Buy' ? 'bg-[#00e676]/15 text-[#00e676]' : 'bg-red-500/15 text-red-400'}`}>{t.side}</span></td>
                <td className="text-white font-semibold">{t.display || t.symbol}</td>
                <td className="mono text-gray-300">{t.volume}</td>
                <td className="mono text-gray-300">{Number(t.open_price).toFixed(2)}</td>
                <td className="mono text-white">{Number(t.close_price).toFixed(2)}</td>
                <td className={`mono font-semibold text-right ${t.pnl >= 0 ? 'text-[#00e676]' : 'text-red-400'}`}>{t.pnl >= 0 ? '+' : ''}${Number(t.pnl).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

export const Settings = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  if (!user) return null;
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-white">Settings</h1>
        <p className="text-sm text-gray-400">Manage your account preferences.</p>
      </div>
      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210] space-y-4">
        <h3 className="text-white font-bold">Profile</h3>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider">Full Name</label>
          <input defaultValue={user.name} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#00e676]"/>
        </div>
        <div>
          <label className="text-xs text-gray-400 uppercase tracking-wider">Email</label>
          <input defaultValue={user.email} className="mt-1 w-full bg-[#050807] border border-[#1a2823] rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#00e676]"/>
        </div>
        <button onClick={() => toast({ title: 'Saved', description: 'Profile updated successfully.' })} className="btn-green px-5 py-2.5 rounded-lg font-semibold">Save Changes</button>
      </div>
      <div className="p-6 rounded-xl border border-[#1a2823] bg-[#0a1210] space-y-3">
        <h3 className="text-white font-bold">Preferences</h3>
        {['Enable 2FA authentication', 'Email trade notifications', 'Push notifications', 'Weekly performance reports'].map((p, i) => (
          <label key={p} className="flex items-center justify-between py-2 border-b border-[#141f1b] last:border-0">
            <span className="text-sm text-gray-300">{p}</span>
            <input type="checkbox" defaultChecked={i < 3} className="w-4 h-4 accent-[#00e676]"/>
          </label>
        ))}
      </div>
    </div>
  );
};
